/** Single source of truth for property facts used across UI, FAQ, schema, and API validation. */
export const PROPERTY_MAX_GUESTS = 8;

export const CANCELLATION_POLICY_SUMMARY =
  "Cancel at least 30 days before check-in for a full refund. Between 7 and 30 days before check-in, half the nightly total is refunded. Within 7 days of check-in the nightly total is non-refundable. The cleaning fee is always returned.";

export const DEFAULT_PROPERTY_ADDRESS =
  "51-636 Kamehameha Highway, Ka'a'awa, HI 96730";

/**
 * The ONLY location string ever shown publicly. The exact street address is
 * private (given to guests after booking); public pages, APIs, schema, and the
 * map must only ever surface this general area.
 */
export const PUBLIC_GENERAL_LOCATION = "Ka'a'awa, Oahu";

/**
 * Split-payment ("pay 50% now") terms. Shown before any deposit is taken, on the
 * confirmation, in the balance reminder, and on the balance payment page, so a guest
 * always sees the consequence of not paying the balance before they commit money.
 */
export const BALANCE_POLICY_SUMMARY =
  "If the remaining balance is not paid by its due date, the reservation is cancelled and the deposit is forfeited under our cancellation policy.";

export const BALANCE_POLICY_SHORT =
  "Balance not paid by the due date cancels the reservation and forfeits the deposit.";

/** Qualitative book-direct savings. No dollar or percent comparisons. */
export const BOOK_DIRECT_SAVINGS =
  "Book direct at the property rate with no Airbnb guest fees or platform markup.";

export const BOOK_DIRECT_SHORT = "Book direct. No platform fees.";

export const STRIPE_SECURE_PAYMENT =
  "Pay securely through Stripe. Card details are encrypted and never stored on our servers.";

export const STRIPE_SECURE_SHORT = "Secured by Stripe";

/**
 * There is no security or equipment deposit. Kept as a named fact rather than
 * deleted: guests and AI assistants both ask, and "none" is a better answer than
 * silence - it is also the honest differentiator against platform listings.
 */
export const SECURITY_DEPOSIT_SUMMARY =
  "No security deposit and no equipment deposit are collected. The water equipment - kayaks, paddles, stand-up paddleboards and surfboards - is free to use; each adult who uses it signs a short water activity waiver.";

export const VERIFIED_STAY_LABEL = "Verified stay";

/** Public-safe policy facts for guest-facing AI drafts. No street address or access codes. */
export const GUEST_REPLY_POLICIES = `Cancellation (matches Airbnb's Firm policy): cancelling at least 30 days before check-in is a full refund. Between 7 and 30 days before check-in, half the nightly total is refunded. Within 7 days of check-in the nightly total is non-refundable. The cleaning fee is always returned, whenever they cancel. A full refund also applies if they cancel within 48 hours of booking and check-in is still at least 14 days away, or within 24 hours of a booking made at least 7 days before check-in. Point them to vacationrentaloahu.co/policies.

House rules: no smoking anywhere on the property. No parties or events without prior written approval. Quiet hours 10:00 PM to 8:00 AM. Maximum 8 guests. No extra visitors without permission. Pets may be allowed with prior written approval and an extra cleaning fee. Service animals are welcome. Children must be supervised near the water.

Payment: book direct at vacationrentaloahu.co/booking. ${BOOK_DIRECT_SAVINGS} ${STRIPE_SECURE_PAYMENT} Split-pay: ${BALANCE_POLICY_SUMMARY} ${SECURITY_DEPOSIT_SUMMARY}

The exact street address, gate code, door code, and WiFi password are sent only after a confirmed booking, in the confirmation and check-in emails. Never share those in a reply.`;

// ---------------------------------------------------------------------------
// Canonical house-rule facts.
//
// These were duplicated across the codebase with conflicting values: the
// policies and stay pages hardcoded quiet hours as 10:00 PM - 8:00 AM, the AI
// guest-reply prompt defaulted to the same, while the email templates defaulted
// to 8:00 PM - 7:00 AM. The property.quiet_hours_start/end columns are NULL, so
// the code defaults were what guests actually saw - two different answers
// depending on which surface they read.
//
// Airbnb listing 45937770 is the source of truth. Anything rendering a house
// rule must read from here (or from the property row where a column exists and
// is populated), never from a local literal.
// ---------------------------------------------------------------------------
export const QUIET_HOURS_START = "8:00 PM";
export const QUIET_HOURS_END = "7:00 AM";

/** Pets are not permitted. The site previously advertised the opposite. */
export const PETS_POLICY = "No pets. Pets are not permitted at the property.";

/** Parties and events are prohibited outright, not subject to approval. */
export const PARTIES_POLICY = "No parties or events. Commercial photography is not permitted.";

export const SMOKING_POLICY = "No smoking anywhere on the property.";
